import {
  CloseButton,
  CrudMode,
  DELETE_WARNING,
  DropDownMenu,
  ListContext,
  ListContextValue,
  MenuItemType,
  Stack,
  Typography,
  UpdateButton,
  ValidationErrors,
  newToast,
} from "@compass/core";
import { DataModelInformation, SubmitButton } from "components";
import { Application, DataModelEnum, Permission, PermissionAction } from "generated/openApi";
import { useContext } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setAppToast } from "redux/reducers/appSlice";
import { isAuthorized } from "services";
import ApiService from "services/apiService";
import { applicationPageRoute } from "./ApplicationPage";

export const ApplicationPageHeader = (props: {
  rootPermission: Permission;
  crudMode: CrudMode;
  fetch: (id: number) => void;
  application: Application;
  setValidationErrors: (errors: ValidationErrors) => void;
  unsavedChanges: boolean;
  reroute: (id: number, crudMode: CrudMode) => void;
  printBlankApplication?: () => void;
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const listContext = useContext<ListContextValue>(ListContext);
  const id = props.application.id;

  const save = (application: Application) => {
    props.setValidationErrors({});

    if (id > 0) {
      ApiService.applications
        .saveUpdate(application)
        .then(() => {
          props.fetch(id);
          listContext.loadList?.();
          dispatch(setAppToast(newToast("success")));
          props.reroute(id, "view");
        })
        .catch((ex) => {
          dispatch(setAppToast(newToast("error")));
          props.setValidationErrors(ex.data);
        });
    } else {
      ApiService.applications
        .saveCreate(application)
        .then((response) => {
          const id = response;
          props.fetch(id);
          props.reroute(id, "view");
          listContext.loadList?.();
          dispatch(setAppToast(newToast("success")));
        })
        .catch((ex) => {
          dispatch(setAppToast(newToast("error")));
          props.setValidationErrors(ex.data);
        });
    }
  };

  const postDelete = (id: number) => {
    if (window.confirm(DELETE_WARNING)) {
      ApiService.applications.delete(id).then(() => {
        navigate("../");
        listContext.loadList?.();
        dispatch(setAppToast(newToast("success")));
      });
    }
  };

  const menuItems: MenuItemType[] = [];

  if (isAuthorized(props.rootPermission, PermissionAction.Delete))
    menuItems.push({
      label: <Typography color="error">Delete</Typography>,
      action: () => postDelete(id),
    });

  return (
    <Stack direction="row" justifyContent="space-between">
      <Stack direction="row" alignItems="center" spacing="sm">
        <Typography variant="inherit">{props.application.name}</Typography>
        {menuItems.length > 0 && <DropDownMenu menuItems={menuItems} />}
      </Stack>

      <Stack direction="row" alignItems="center" spacing="sm">
        <DataModelInformation dataModelType={DataModelEnum.Application} dataModelId={id} />

        {props.printBlankApplication && <UpdateButton label="Print Blank" onClick={props.printBlankApplication} />}

        <CloseButton confirm={props.unsavedChanges} />

        {props.crudMode === "edit" && (
          <SubmitButton onClick={() => save(props.application!)} disabled={!props.unsavedChanges} />
        )}

        {props.crudMode === "view" && isAuthorized(props.rootPermission, PermissionAction.Update) && (
          <UpdateButton onClick={() => props.reroute(id, "edit")} />
        )}
      </Stack>
    </Stack>
  );
};
